import {computed, ref} from "vue";
import type {Tag} from "@/Types";
import {getTags as apiGetTags, storeTag as apiStoreTag} from "@/Services/tags";

const tags = ref<Tag[]>([]);
const pendingRequests = ref(0);
const loaded = ref(false);
let fetchPromise: Promise<Tag[]> | null = null;

const byName = (a: Tag, b: Tag) => a.name.localeCompare(b.name, undefined, {sensitivity: "base"});

const normalizeTags = (items: Tag[]): Tag[] => {
    return [...items]
        .filter((item) => !!item?.name)
        .reduce((accumulator: Tag[], item) => {
            if (!accumulator.some((tag) => tag.name === item.name)) {
                accumulator.push(item);
            }

            return accumulator;
        }, [])
        .sort(byName);
};

const addOrUpdateTag = (tag: Tag): void => {
    const nextTags = [...tags.value];
    const existingIndex = nextTags.findIndex((item) => item.name === tag.name);

    if (existingIndex === -1) {
        nextTags.push(tag);
    } else {
        nextTags[existingIndex] = tag;
    }

    tags.value = normalizeTags(nextTags);
};

const fetchTags = async (params: Record<string, unknown> = {}): Promise<Tag[]> => {
    const hasCustomParams = Object.keys(params).length > 0;

    if (!hasCustomParams && loaded.value) {
        return tags.value;
    }

    if (!hasCustomParams && fetchPromise) {
        return fetchPromise;
    }

    pendingRequests.value++;

    const request = apiGetTags(params)
        .then(({data}) => {
            const fetchedTags = normalizeTags(Array.isArray(data) ? data : []);

            if (!hasCustomParams) {
                tags.value = fetchedTags;
                loaded.value = true;
            }

            return fetchedTags;
        })
        .finally(() => {
            pendingRequests.value = Math.max(0, pendingRequests.value - 1);
            if (!hasCustomParams) {
                fetchPromise = null;
            }
        });

    if (!hasCustomParams) {
        fetchPromise = request;
    }

    return request;
};

const createTag = async (name: string): Promise<Tag> => {
    const normalizedName = name.trim();
    if (!normalizedName) {
        throw new Error("Tag name is required");
    }

    pendingRequests.value++;

    try {
        const {tag} = await apiStoreTag({tag: normalizedName});
        addOrUpdateTag(tag);

        return tag;
    } finally {
        pendingRequests.value = Math.max(0, pendingRequests.value - 1);
    }
};

export const useTags = () => {
    return {
        tags,
        loaded,
        loading: computed(() => pendingRequests.value > 0),
        fetchTags,
        createTag,
    };
};
